import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import {
    AppConfigModel,
    BuildInfoModel,
    UrlConfigModel
} from '@models/shared/shared-models';
import { lastValueFrom } from 'rxjs';
import { appRoutes } from '../../../app.routes';
import { RouteDictionaryService } from '@services/infrastructure/route-dictionary.service';
import { TenantInfoService } from "@services/account/tenant-info.service";
import { Router } from "@angular/router";

export function initializeApp(appInitService: AppInitService) {
    return () => appInitService.initializeApp();
}

@Injectable({
    providedIn: 'root'
})

export class AppInitService {

    private httpClient: HttpClient;

    constructor(httpBackend: HttpBackend,
                private routeService: RouteDictionaryService,
                private tenantService: TenantInfoService,
                private router: Router) {
        this.httpClient = new HttpClient(httpBackend);
    }

    async initializeApp(): Promise<any> {
        const salt = (new Date()).getTime();
        let buildInfoUrl = `/assets/buildInfo.json?${salt}`;
        let appConfigUrl = `/assets/app.config.json?${salt}`;

        environment.appConfig = await lastValueFrom(this.httpClient.get<AppConfigModel>(appConfigUrl));
        environment.buildInfo = await lastValueFrom(this.httpClient.get<BuildInfoModel>(buildInfoUrl));
        environment.routeDictionary = await lastValueFrom(this.routeService.generateRouteDictionary(appRoutes));
        this.setupUrl(environment.urlStore);
        environment.activeTenants = await lastValueFrom(this.tenantService.getTenants());
        this.captureTenant();
    }

    // api urls per env come from app.config.json (no service locator)
    private setupUrl(urlModels: UrlConfigModel[]) {
        urlModels.forEach(value => {
            if (!value.overrideUrl)
                value.overrideUrl = environment.appConfig.serviceUrls[value.artifactName];

            let scopeList = [];
            if (value.scopes && value.scopes.length) {
                value.scopes.forEach(scope => {
                    scope = scope.replace("{env}", environment.appConfig.environment);
                    scopeList.push(scope);
                })
            }

            value.scopes = scopeList;
        });
    }

    private captureTenant() {
        let currentUrl = document.documentURI.replace(document.baseURI, '');
        const parsedUrl = parseUrl(currentUrl);
        let refCodeVal = parsedUrl.queryParams['rc'];
        let currentUrlArr = parsedUrl.route.split('/');

        //If client came with referral code, capture it
        if (refCodeVal) {
            let metaData = TenantInfoService.getTenantMetaData();
            metaData.tenantReferralCode = refCodeVal;
            this.tenantService.saveTenantMetaData(metaData);
        }

        //if client came with tenant name, capture it
        if (currentUrlArr.length >= 1) {
            let tenantName = currentUrlArr[0];
            tenantName = tenantName.toLowerCase();
            let validTenant = environment.activeTenants.includes(tenantName);
            if (validTenant) {
                //Save in local storage
                let metaData = TenantInfoService.getTenantMetaData();
                metaData.tenantIdentifier = tenantName;
                this.tenantService.saveTenantMetaData(metaData);

                //Remove tenant name from url and carry on
                currentUrlArr.splice(0, 1);
                let newUrl = currentUrlArr.join('/');
                if (parsedUrl.queryString)
                    newUrl += `?${parsedUrl.queryString}`;

                this.router.navigateByUrl(newUrl).then();
            }
        }

        function parseUrl(url: string): {
            route: string; queryString: string,
            queryParams: Record<string, string>
        } {
            // Split the URL into the route and the query string parts
            const [ route, queryString ] = url.split('?');

            // Initialize an empty object to hold the query parameters
            const queryParams: Record<string, string> = {};

            // Check if there's a query string
            if (queryString) {
                // Split the query string into key-value pairs
                const pairs = queryString.split('&');

                // Iterate over the key-value pairs and add them to the queryParams object
                for (const pair of pairs) {
                    const [ key, value ] = pair.split('=');
                    queryParams[decodeURIComponent(key)] = decodeURIComponent(value);
                }
            }

            // Return the route and the query parameters
            return { route, queryString, queryParams };
        }
    }
}